import { displayName, formatWhen } from './format';

const ACTION_OPTIONS = [
  { value: '', label: 'All actions' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'work_started', label: 'Work started' },
  { value: 'work_stopped', label: 'Work stopped' },
  { value: 'parts_requested', label: 'Parts requested' },
  { value: 'repair_recommended', label: 'Repair recommended' },
  { value: 'checklist_updated', label: 'Checklist updated' },
  { value: 'inspection_submitted', label: 'Inspection submitted' },
  { value: 'qc_requested', label: 'QC requested' },
  { value: 'qc_approved', label: 'QC approved' },
  { value: 'qc_rejected', label: 'QC rejected' },
];

const UUID_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

export function actionLabel(action) {
  return ACTION_OPTIONS.find((item) => item.value === action)?.label || action;
}

export { ACTION_OPTIONS };

function stripUuids(text) {
  if (!text) return '';
  return text
    .replace(UUID_RE, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+·\s*$/g, '')
    .trim();
}

export function visitLabel(row) {
  const parts = [row.service_type, row.vehicle_title].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'workshop visit';
}

export function activitySummary(row) {
  const visit = visitLabel(row);

  switch (row.action) {
    case 'assigned':
      return `Assigned to ${visit}`;
    case 'work_started':
      return `Started work on ${visit}`;
    case 'work_stopped': {
      const minutesMatch = row.summary?.match(/(\d+)\s+minutes/i);
      return minutesMatch
        ? `Stopped work after ${minutesMatch[1]} minutes on ${visit}`
        : `Stopped work on ${visit}`;
    }
    case 'parts_requested':
      if (row.summary?.startsWith('Requested part:')) {
        return `${row.summary} for ${visit}`;
      }
      return `Requested parts for ${visit}`;
    case 'repair_recommended':
      if (row.summary?.startsWith('Recommended repair:')) {
        return `${row.summary} on ${visit}`;
      }
      return `Added repair recommendation for ${visit}`;
    case 'checklist_updated':
      return `Updated checklist for ${visit}`;
    case 'inspection_submitted':
      return `Submitted inspection for ${visit}`;
    case 'qc_requested':
      return `Requested QC for ${visit}`;
    case 'qc_approved':
      return `QC approved for ${visit}`;
    case 'qc_rejected':
      return `QC rejected for ${visit}`;
    default: {
      const cleaned = stripUuids(row.summary);
      if (cleaned && !/^assigned to job card\s*$/i.test(cleaned)) {
        return cleaned;
      }
      return `${actionLabel(row.action)} on ${visit}`;
    }
  }
}

export function matchesActivitySearch(row, needle) {
  if (!needle) return true;
  const hay = [
    activitySummary(row),
    row.technician_name,
    row.vehicle_title,
    row.service_type,
    actionLabel(row.action),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(needle);
}

export function initialsFor(row) {
  const name = displayName(row);
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

export function withinDateRange(createdAt, range) {
  if (!range || range === 'all') return true;
  const at = new Date(createdAt);
  if (Number.isNaN(at.getTime())) return false;
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;
  if (range === 'today') return now - at.getTime() <= day;
  if (range === '7d') return now - at.getTime() <= 7 * day;
  if (range === '30d') return now - at.getTime() <= 30 * day;
  return true;
}

export { displayName, formatWhen };
